import * as Tone from 'tone';

// Simplified synthesizer manager (fixes audio connections)
export class SynthManager {
    constructor() {
        this.synths = new Map();
        this.masterGain = null;
        this.outputResolver = null; // optional: function(trackId) => AudioNode
    }

    initialize(outputOrMaster) {
        if (typeof outputOrMaster === 'function') {
            this.outputResolver = outputOrMaster;
            this.masterGain = null;
            console.log('🎛️ SynthManager initialized with output resolver');
        } else {
            this.masterGain = outputOrMaster;
            this.outputResolver = null;
            console.log('🎛️ SynthManager initialized with master gain:', !!outputOrMaster);
        }
    }

    createSynth(trackId, instrument = 'piano') {
        console.log(`🎹 Creating synth for track ${trackId}, instrument: ${instrument}`);

        if (!this.masterGain) {
            console.error('❌ Master gain not initialized');
            return null;
        }

        try {
            // Clean up old synthesizer
            if (this.synths.has(trackId)) {
                const oldSynth = this.synths.get(trackId);
                oldSynth.dispose();
                this.synths.delete(trackId);
                console.log(`🗑️ Disposed old synth for track ${trackId}`);
            }

            // Create new synthesizer
            let synth;
            if (instrument === 'piano') {
                synth = new Tone.PolySynth(Tone.Synth, {
                    oscillator: { type: 'sine' },
                    envelope: { attack: 0.02, decay: 0.1, sustain: 0.3, release: 1 }
                });
            } else if (instrument === 'drums') {
                synth = new Tone.PolySynth(Tone.MembraneSynth, {
                    oscillator: { type: 'sine' },
                    envelope: { attack: 0.001, decay: 0.4, sustain: 0.01, release: 1.4 }
                });
            } else if (instrument === 'bass') {
                synth = new Tone.MonoSynth({
                    oscillator: { type: 'sawtooth' },
                    filter: { Q: 2, frequency: 120 },
                    envelope: { attack: 0.02, decay: 0.3, sustain: 0.7, release: 0.8 }
                });
            } else if (instrument === 'synth') {
                synth = new Tone.PolySynth(Tone.Synth, {
                    oscillator: { type: 'square' },
                    envelope: { attack: 0.1, decay: 0.2, sustain: 0.5, release: 0.8 }
                });
            } else {
                synth = new Tone.PolySynth(Tone.Synth);
            }

            // Connect to routing destination if provided, otherwise master
            if (this.outputResolver) {
                const destination = this.outputResolver(trackId);
                synth.connect(destination);
            } else if (this.masterGain) {
                synth.connect(this.masterGain);
            } else {
                console.warn('No synth destination available; connecting to destination()');
                synth.toDestination();
            }
            this.synths.set(trackId, synth);

            console.log(`✅ Synth created and connected for track ${trackId}`);

            return synth;
        } catch (error) {
            console.error(`❌ Failed to create synth for track ${trackId}:`, error);
            return null;
        }
    }

    getSynth(trackId) {
        const synth = this.synths.get(trackId);
        if (!synth) {
            console.warn(`⚠️ No synth found for track ${trackId}, available tracks:`, Array.from(this.synths.keys()));
        }
        return synth;
    }

    disposeSynth(trackId) {
        if (this.synths.has(trackId)) {
            const synth = this.synths.get(trackId);
            try {
                synth.dispose();
            } catch (error) {
                console.warn(`Error disposing synth ${trackId}:`, error);
            }
            this.synths.delete(trackId);
            console.log(`🗑️ Synth disposed for track ${trackId}`);
        }
    }

    disposeAll() {
        console.log('🗑️ Disposing all synths');
        this.synths.forEach((synth, trackId) => {
            try {
                synth.dispose();
            } catch (error) {
                console.warn(`Error disposing synth ${trackId}:`, error);
            }
        });
        this.synths.clear();
    }
} 